import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MetOnField from "@/components/meeting/MetOnField";
import { Textarea } from "@/components/ui/textarea";
import { User, Building2, Briefcase, Mail, Phone, FileText, Globe, ExternalLink } from "lucide-react";
import { formatPhoneAsTyped } from "@/lib/phone";
import { capitalizeWords } from "@/lib/titleCase";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s\-().]{7,15}$/;

export default function MeetingForm({ formData, setFormData, lowConfidenceFields = [], onSaveContact, isSaving, repId = 'adam', show = null }) {
  const [touched, setTouched] = useState({});

  const isLowConfidence = (field) => lowConfidenceFields.includes(field);

  // email_source / phone_source (task: source labels, 2026-09-24): a
  // keystroke in either field is, by definition, a manual edit, whether it
  // started blank or is overwriting whatever OCR (BadgeScanner's
  // onDataExtracted, handled in NewMeeting.jsx) had put there. Every other
  // field this is called for (first_name, company, meeting_topics, ...)
  // has no source column at all, so this is a no-op for them.
  const update = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      ...(field === 'email' && { email_source: 'manual' }),
      ...(field === 'phone' && { phone_source: 'manual' }),
    }));
  };

  const updatePhone = (field, value) => update(field, formatPhoneAsTyped(value));

  const FREE_EMAIL_DOMAINS = ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com','live.com','msn.com','protonmail.com','googlemail.com','ymail.com'];

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email' && formData.email) {
      const cleanEmail = formData.email.replace(/\s+/g, '');
      const domain = cleanEmail.split('@')[1]?.toLowerCase();
      setFormData(prev => {
        const updated = { ...prev, email: cleanEmail };
        if (domain && !FREE_EMAIL_DOMAINS.includes(domain) && !prev.website) {
          updated.website = `www.${domain}`;
        }
        return updated;
      });
    }
    if (field === 'website' && formData.website) {
      setFormData(prev => ({ ...prev, website: prev.website.replace(/\s+/g, '') }));
    }
  };

  const emailError = touched.email && formData.email && !EMAIL_REGEX.test(formData.email)
    ? 'Enter a valid email address' : null;
  const phoneError = touched.phone && formData.phone && !PHONE_REGEX.test(formData.phone)
    ? 'Enter a valid phone number' : null;

  return (
    <div className="space-y-5">
      {lowConfidenceFields.length > 0 && (
        <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20 text-xs text-destructive">
          <span className="font-semibold shrink-0">⚠ Please verify:</span>
          <span>Fields highlighted in red had low OCR confidence. Check them before saving.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">First Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="First name"
              autoComplete="given-name"
              value={formData.first_name || ''}
              onChange={(e) => update('first_name', e.target.value)}
              className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${isLowConfidence('first_name') ? 'ring-2 ring-destructive text-destructive' : ''}`}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Last Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Last name"
              autoComplete="family-name"
              value={formData.last_name || ''}
              onChange={(e) => update('last_name', e.target.value)}
              className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${isLowConfidence('last_name') ? 'ring-2 ring-destructive text-destructive' : ''}`}
            />
          </div>
        </div>
      </div>

      <MetOnField
        value={formData.captured_at}
        onChange={(iso) => update('captured_at', iso)}
        show={show}
      />

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</Label>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Company name"
            autoComplete="organization"
            value={formData.company || ''}
            onChange={(e) => update('company', e.target.value)}
            autoCapitalize="none"
            className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${isLowConfidence('company') ? 'ring-2 ring-destructive text-destructive' : ''}`}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Job Title</Label>
        <div className="relative">
          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Job title"
            autoComplete="organization-title"
            value={formData.title || ''}
            onChange={(e) => update('title', capitalizeWords(e.target.value))}
            className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${isLowConfidence('title') ? 'ring-2 ring-destructive text-destructive' : ''}`}
          />
        </div>
      </div>

      <div className="h-px bg-border" />

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Email Address</Label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="email"
            placeholder="email@company.com"
            autoComplete="email"
            inputMode="email"
            value={formData.email || ''}
            onChange={(e) => update('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${emailError || isLowConfidence('email') ? 'ring-2 ring-destructive text-destructive' : ''}`}
          />
        </div>
        {emailError && <p className="text-xs text-destructive">{emailError}</p>}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Cell Number</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="tel"
            placeholder="(555) 000-0000"
            autoComplete="mobile tel"
            value={formData.phone || ''}
            onChange={(e) => updatePhone('phone', e.target.value)}
            onBlur={() => handleBlur('phone')}
            className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${phoneError || isLowConfidence('phone') ? 'ring-2 ring-destructive text-destructive' : ''}`}
          />
        </div>
        {phoneError && <p className="text-xs text-destructive">{phoneError}</p>}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Office Number</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="tel"
              placeholder="Office phone"
              autoComplete="work tel"
              value={formData.office_phone || ''}
              onChange={(e) => updatePhone('office_phone', e.target.value)}
              onBlur={() => handleBlur('office_phone')}
              className={`pl-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${isLowConfidence('office_phone') ? 'ring-2 ring-destructive text-destructive' : ''}`}
            />
          </div>
          <div className="w-24">
            <Input
              placeholder="Ext."
              value={formData.office_phone_ext || ''}
              onChange={(e) => update('office_phone_ext', e.target.value)}
              className="h-11 bg-secondary/50 border-0 focus-visible:ring-accent text-center"
            />
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Website</Label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="url"
            placeholder="www.company.com"
            value={formData.website || ''}
            onChange={(e) => update('website', e.target.value)}
            onBlur={() => handleBlur('website')}
            className={`pl-10 pr-10 h-11 bg-secondary/50 border-0 focus-visible:ring-accent ${isLowConfidence('website') ? 'ring-2 ring-destructive text-destructive' : ''}`}
          />
          {formData.website && (
            <a
              href={formData.website.startsWith('http') ? formData.website : `https://${formData.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-accent hover:text-accent/70"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>



      <div className="h-px bg-border" />

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Meeting Topics</Label>
        <p className="text-[11px] text-muted-foreground -mt-1">Included in the follow up email</p>
        <div className="relative">
          <FileText className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
          <Textarea
            placeholder={show?.meeting_topics_placeholder || 'What did you talk about?'}
            value={formData.meeting_topics || ''}
            onChange={(e) => update('meeting_topics', e.target.value)}
            className="pl-10 min-h-[70px] bg-secondary/50 border-0 focus-visible:ring-accent resize-none"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Internal Notes</Label>
        <p className="text-[11px] text-muted-foreground -mt-1">Private, only visible to you in Zoom (locations, price quoted, setup fees, etc.)</p>
        <div className="relative">
          <FileText className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
          <Textarea
            placeholder="# of locations, price quoted, setup fees, follow-up items..."
            value={formData.internal_notes || ''}
            onChange={(e) => update('internal_notes', e.target.value)}
            className="pl-10 min-h-[80px] bg-secondary/50 border-0 focus-visible:ring-accent resize-none"
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Note for Emails</Label>
        <p className="text-[11px] text-muted-foreground -mt-1">Shown to the contact, at the end of every email to them. Never in a text.</p>
        <div className="relative">
          <FileText className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
          <Textarea
            placeholder="Anything you want to add to the emails this contact gets..."
            value={formData.email_note || ''}
            onChange={(e) => update('email_note', e.target.value)}
            className="pl-10 min-h-[60px] bg-secondary/50 border-0 focus-visible:ring-accent resize-none"
          />
        </div>
      </div>
    </div>
  );
}